// The course library's small rules, shared and tested (28.10.2026).

import { ACCEPT_ATTR, extensionOf, fileProblem } from "./portalFiles";

const TZ = "Europe/Bucharest";

/** "Încarcă PowerPoint" / "Încarcă PDF" / "Încarcă un fișier": what a slot takes. */
export function uploadLabel(kind, audience) {
  if (kind === "presentation") return audience === "trainers" ? "Încarcă PowerPoint" : "Încarcă PDF";
  if (kind === "assignment") return "Atașează un fișier";
  return "Încarcă un fișier";
}

const PRESENTATION_EXT = { trainers: ["pptx", "ppt", "odp"], participants: ["pdf"] };

/** What the file picker offers for a slot: only a presentation is strict. */
export function acceptFor(kind, audience) {
  const exts = kind === "presentation" ? PRESENTATION_EXT[audience] : null;
  return exts ? exts.map((e) => `.${e}`).join(",") : ACCEPT_ATTR;
}

/** Null when the file may go in this slot, else the sentence to read — the
 *  same rules the server applies, said before anything is saved. */
export function slotProblem(kind, audience, file) {
  const exts = kind === "presentation" ? PRESENTATION_EXT[audience] : null;
  if (exts && !exts.includes(extensionOf(file?.name))) {
    return `${file?.name || "Fișierul"}: aici merge ${audience === "trainers" ? "PowerPoint (.pptx, .ppt) sau .odp" : "doar PDF"}.`;
  }
  return fileProblem(file);
}

/** "1 oră" / "0,5 ore" — Romanian decimal comma. */
export function hoursText(h) {
  const v = Math.round(Number(h || 0) * 100) / 100;
  return v === 1 ? "1 oră" : `${String(v).replace(".", ",")} ore`;
}

/** What an assignment's settings say, as one line of chips. */
export function assignmentFacts(item, componentLabels = {}) {
  if (item.kind !== "assignment") return [];
  return [
    `${hoursText(item.hours)} asincron`,
    componentLabels[item.component] || item.component,
    `termen: ${item.due_days} ${item.due_days === 1 ? "zi" : "zile"} după sesiunea lecției, 23:59`,
    item.required ? "obligatorie" : "opțională",
    item.hard_close ? "nu se mai predă după termen" : "se poate preda și după termen",
  ];
}

/** "v3 · 28 oct., 14:05 · Ana" — a version, as the list shows it. */
export function versionLabel(v) {
  if (!v) return "";
  const when = v.at || v.updated_at;
  const at = when
    ? new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
      .format(new Date(when))
    : "";
  return [`v${v.version}`, at, v.by || v.updated_by].filter(Boolean).join(" · ");
}

/** One link per line, optionally "Name | https://…". */
export function linksFromText(text) {
  return String(text || "").split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
    const [a, b] = line.includes("|") ? line.split("|").map((x) => x.trim()) : ["", line];
    return { label: b ? a : "", url: b || a };
  });
}

export function linksToText(links) {
  return (links || []).map((l) => (l.label ? `${l.label} | ${l.url}` : l.url)).join("\n");
}

/** "v2 în 3 grupe, v1 în 1 grupă" — where an item's versions are. */
export function groupsLine(inGroups) {
  return (inGroups || []).map((x) => `v${x.version} în ${x.groups} ${x.groups === 1 ? "grupă" : "grupe"}`).join(", ");
}

/** Whether "Aplică în grupele în curs" has something to do: running groups
 *  exist and some group holds an older version, or none holds this item yet. */
export function runningBehind(item, runningGroups) {
  if (!runningGroups) return false;
  const inGroups = item.in_groups || [];
  return inGroups.length === 0 || inGroups.some((x) => x.version !== item.version);
}

/** The toast after applying a version to the running groups. */
export function appliedText(r) {
  if (!r || (!r.updated && !r.added)) return "Nicio grupă în curs nu avea nevoie de schimbare.";
  const parts = [];
  if (r.updated) parts.push(`actualizat în ${r.updated} ${r.updated === 1 ? "grupă" : "grupe"}`);
  if (r.added) parts.push(`adăugat în ${r.added} ${r.added === 1 ? "grupă" : "grupe"}`);
  const kept = r.kept_hours ? ` La ${r.kept_hours} ${r.kept_hours === 1 ? "temă" : "teme"} cu predări, orele au rămas.` : "";
  return `${parts.join(", ")[0].toUpperCase()}${parts.join(", ").slice(1)}.${kept}`;
}
